import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';

import { leadBookerPropTypes } from './PropTypes';

import {
  PCA_PREDICT_FIND_URL,
  PCA_PREDICT_RETRIEVE_URL,
} from '../../../config/EnvironmentVariables.config';
import jsonGet from '../../../util/jsonGet';

import Button from '../Atoms/Button';
import Input from '../Atoms/Input';
import InternationalPhoneCodeDropdown from '../Atoms/InternationalPhoneCodeDropdown';
import AddressPicker from './AddressPicker';

const propTypes = exact({
  apiKey: PropTypes.string,
  buttonText: PropTypes.string,
  fieldNamePrefix: PropTypes.string.isRequired,
  fieldset: leadBookerPropTypes.isRequired,
  handleAddressPopulate: PropTypes.func,
  handleFieldChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  excludeDialCodes: PropTypes.bool,
  dialCodes: PropTypes.arrayOf(PropTypes.string),
  postcodePlaceholderText: PropTypes.string,
  addressLine1PlaceholderText: PropTypes.string,
  addressLine2PlaceholderText: PropTypes.string,
  cityPlaceholderText: PropTypes.string,
  telephonePlaceholderText: PropTypes.string,
  mobilePhonePlaceholderText: PropTypes.string,
  emailAddressPlaceholderText: PropTypes.string,
});

const defaultProps = {
  apiKey: null,
  buttonText: 'Find address',
  handleAddressPopulate: () => false,
  handleFieldChange: () => false,
  isDisabled: false,
  excludeDialCodes: true,
  dialCodes: [],
  postcodePlaceholderText: 'Postcode',
  addressLine1PlaceholderText: 'Address Line 1',
  addressLine2PlaceholderText: 'Address Line 2',
  cityPlaceholderText: 'City',
  telephonePlaceholderText: 'Phone',
  mobilePhonePlaceholderText: 'Mobile',
  emailAddressPlaceholderText: 'Email Address',
};

class MemberAddress extends Component {
  constructor(props) {
    super(props);

    this.handleAddressClick = this.handleAddressClick.bind(this);
    this.handleFindAddressClick = this.handleFindAddressClick.bind(this);

    this.state = {
      pcaPredictAddressList: null,
      pcaPredictLoading: false,
    };
  }

  getAddressList(text, container) {
    const options = {
      Countries: 'GB',
      Key: this.props.apiKey,
      Language: 'en-gb',
      Limit: 100,
      Text: text,
    };

    if (container) options.Container = container;

    jsonGet(
      PCA_PREDICT_FIND_URL,
      options,
      (response) => {
        const state = { pcaPredictLoading: false };

        // No results or API error
        if (response.Items.length < 1 || response.Items[0].Error) {
          this.setState(Object.assign(state, { pcaPredictAddressList: [] }));
        }

        // 1 area returned, get address list
        if (response.Items.length === 1 && response.Items[0].Type === 'Postcode') {
          this.getAddressList(this.props.fieldset.address?.postcode, response.Items[0].Id);
        }

        // Multiple addresses returned
        this.setState(Object.assign(state, { pcaPredictAddressList: response.Items }));
      },
      false
    );
  }

  getAddress(itemID) {
    const options = {
      Id: itemID,
      Key: this.props.apiKey,
    };

    jsonGet(
      PCA_PREDICT_RETRIEVE_URL,
      options,
      (response) => {
        // No results or API error
        if (response.Items.length < 1 || response.Items[0].Error) {
          this.setState({ pcaPredictAddressList: [] });
        }

        const { address } = this.props.fieldset;

        const fetchedAddress = {
          ...address,
          addressLine1: {
            ...address.addressLine1,
            value: response.Items[0].Line1,
          },
          addressLine2: {
            ...address.addressLine2,
            value: response.Items[0].Line2,
          },
          city: {
            ...address.city,
            value: response.Items[0].City,
          },
          postcode: {
            ...address.postcode,
            value: response.Items[0].PostalCode,
          },
        };

        this.setState({ pcaPredictAddressList: null });
        this.props.handleAddressPopulate(fetchedAddress);
      },
      false
    );
  }

  handleFindAddressClick() {
    this.setState({ pcaPredictLoading: true });
    this.getAddressList(this.props.fieldset.address?.postcode.value);
  }

  handleAddressClick(item) {
    if (item.Type === 'Postcode')
      this.getAddressList(this.props.fieldset.address?.postcode.value, item.Id);
    else this.getAddress(item.Id);
  }

  render() {
    return (
      <div className="MemberDetails">
        <div className="MemberDetails_group">
          <div className="MemberDetails_item MemberDetails_item-1/3">
            {/*
              As chrome ignores autocomplete="off"
              A suggestion workaround from chromium.org is
              to give it an attribute that's not recognized.
              See: https://bugs.chromium.org/p/chromium/issues/detail?id=587466#c10
            */}
            <Input
              autoComplete="custom-postcode-field"
              blockName="MemberDetails_field"
              fieldName={`${this.props.fieldNamePrefix}.address.postcode`}
              fieldValue={
                this.props.isDisabled
                  ? this.props.fieldset.address?.postcode
                  : this.props.fieldset.address?.postcode.value
              }
              handleChange={this.props.handleFieldChange}
              inputType="text"
              isDisabled={this.props.isDisabled}
              isInvalid={this.props.fieldset.address?.postcode.isInvalid}
              isRequired={this.props.fieldset.address?.postcode.isRequired}
              modifier="ghost"
              maxLength={13}
              placeholderText={this.props.postcodePlaceholderText}
            />
          </div>
          {!this.props.isDisabled && (
            <div className="MemberDetails_item MemberDetails_item-2/3">
              <Button
                buttonType="button"
                handleClick={this.handleFindAddressClick}
                isDisabled={this.props.fieldset.address?.postcode === ''}
                isLoading={this.state.pcaPredictLoading}
                modifier="ghost"
                text={this.props.buttonText}
              />
            </div>
          )}
        </div>
        {this.state.pcaPredictAddressList && (
          <div className="MemberDetails_group">
            <div className="MemberDetails_item MemberDetails_item-1">
              <AddressPicker
                handleButtonClick={this.handleAddressClick}
                items={this.state.pcaPredictAddressList}
              />
            </div>
          </div>
        )}
        <div className="MemberDetails_group">
          <div className="MemberDetails_item MemberDetails_item-1/3">
            <Input
              autoComplete="custom-addressLine1-field"
              blockName="MemberDetails_field"
              fieldName={`${this.props.fieldNamePrefix}.address.addressLine1`}
              fieldValue={
                this.props.isDisabled
                  ? this.props.fieldset.address?.addressLine1
                  : this.props.fieldset.address?.addressLine1.value
              }
              handleChange={this.props.handleFieldChange}
              inputType="text"
              isDisabled={this.props.isDisabled}
              isInvalid={this.props.fieldset.address?.addressLine1.isInvalid}
              isRequired={this.props.fieldset.address?.addressLine1.isRequired}
              modifier="ghost"
              placeholderText={this.props.addressLine1PlaceholderText}
            />
          </div>
          <div className="MemberDetails_item MemberDetails_item-1/3">
            <Input
              autoComplete="custom-addressLine2-field"
              blockName="MemberDetails_field"
              fieldName={`${this.props.fieldNamePrefix}.address.addressLine2`}
              fieldValue={
                this.props.isDisabled
                  ? this.props.fieldset.address?.addressLine2
                  : this.props.fieldset.address?.addressLine2.value
              }
              handleChange={this.props.handleFieldChange}
              inputType="text"
              isDisabled={this.props.isDisabled}
              isInvalid={this.props.fieldset.address?.addressLine2.isInvalid}
              isRequired={this.props.fieldset.address?.addressLine2.isRequired}
              modifier="ghost"
              placeholderText={this.props.addressLine2PlaceholderText}
            />
          </div>
          <div className="MemberDetails_item MemberDetails_item-1/3">
            <Input
              autoComplete="custom-city-field"
              blockName="MemberDetails_field"
              fieldName={`${this.props.fieldNamePrefix}.address.city`}
              fieldValue={
                this.props.isDisabled
                  ? this.props.fieldset.address?.city
                  : this.props.fieldset.address?.city.value
              }
              handleChange={this.props.handleFieldChange}
              inputType="text"
              isDisabled={this.props.isDisabled}
              isInvalid={this.props.fieldset.address?.city.isInvalid}
              isRequired={this.props.fieldset.address?.city.isRequired}
              modifier="ghost"
              placeholderText={this.props.cityPlaceholderText}
            />
          </div>
        </div>
        <div className="MemberDetails_group">
          <div className="MemberDetails_item MemberDetails_item-1/3">
            <div className="MemberDetails_multiItemContainer">
              <div className="MemberDetails_item-2/5">
                <InternationalPhoneCodeDropdown
                  fieldName={`${this.props.fieldNamePrefix}.phoneCountryDialCode`}
                  fieldValue={
                    this.props.isDisabled
                      ? this.props.fieldset.phoneCountryDialCode
                      : this.props.fieldset.phoneCountryDialCode.value
                  }
                  handleChange={this.props.handleFieldChange}
                  isDisabled={this.props.isDisabled}
                  modifier="ghost"
                  excludeDialCodes={this.props.excludeDialCodes}
                  dialCodes={this.props.dialCodes}
                />
              </div>
              <div className="MemberDetails_item-3/5">
                <Input
                  blockName="MemberDetails_field"
                  fieldName={`${this.props.fieldNamePrefix}.phone`}
                  fieldValue={
                    this.props.isDisabled
                      ? this.props.fieldset.phone
                      : this.props.fieldset.phone.value
                  }
                  handleChange={this.props.handleFieldChange}
                  inputType="text"
                  isDisabled={this.props.isDisabled}
                  isInvalid={this.props.fieldset.phone.isInvalid}
                  isRequired={this.props.fieldset.phone.isRequired}
                  modifier="ghost"
                  placeholderText={this.props.telephonePlaceholderText}
                />
              </div>
            </div>
          </div>
          <div className="MemberDetails_item MemberDetails_item-1/3">
            <div className="MemberDetails_multiItemContainer">
              <div className="MemberDetails_item-2/5">
                <InternationalPhoneCodeDropdown
                  fieldName={`${this.props.fieldNamePrefix}.mobileCountryDialCode`}
                  fieldValue={
                    this.props.isDisabled
                      ? this.props.fieldset.mobileCountryDialCode
                      : this.props.fieldset.mobileCountryDialCode.value
                  }
                  handleChange={this.props.handleFieldChange}
                  isDisabled={this.props.isDisabled}
                  modifier="ghost"
                  excludeDialCodes={this.props.excludeDialCodes}
                  dialCodes={this.props.dialCodes}
                />
              </div>
              <div className="MemberDetails_item-3/5">
                <Input
                  blockName="MemberDetails_field"
                  fieldName={`${this.props.fieldNamePrefix}.mobile`}
                  fieldValue={
                    this.props.isDisabled
                      ? this.props.fieldset.mobile
                      : this.props.fieldset.mobile.value
                  }
                  handleChange={this.props.handleFieldChange}
                  inputType="text"
                  isDisabled={this.props.isDisabled}
                  isInvalid={this.props.fieldset.mobile.isInvalid}
                  isRequired={this.props.fieldset.mobile.isRequired}
                  modifier="ghost"
                  placeholderText={this.props.mobilePhonePlaceholderText}
                />
              </div>
            </div>
          </div>
          <div className="MemberDetails_item MemberDetails_item-1/3">
            <Input
              blockName="MemberDetails_field"
              fieldName={`${this.props.fieldNamePrefix}.emailAddress`}
              fieldValue={
                this.props.isDisabled
                  ? this.props.fieldset.emailAddress
                  : this.props.fieldset.emailAddress.value
              }
              handleChange={this.props.handleFieldChange}
              inputType="email"
              isDisabled={this.props.isDisabled}
              isInvalid={this.props.fieldset.emailAddress.isInvalid}
              isRequired={this.props.fieldset.emailAddress.isRequired}
              modifier="ghost"
              placeholderText={this.props.emailAddressPlaceholderText}
            />
          </div>
        </div>
      </div>
    );
  }
}

MemberAddress.propTypes = propTypes;
MemberAddress.defaultProps = defaultProps;

export default MemberAddress;
